import { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { MessageProvider } from "./MessageContext";
import { AuthContext } from "./AuthContext";
import { WorkspaceContext } from "./WorkspaceContext";
import { socket } from "../services/socket";

export const DisplayDiscussionForMiniScreenContext = createContext();

function roomKey(workspaceId, rawRoomId) {
  return `${workspaceId}:${rawRoomId}`;
}

function rawRoomFromKey(key) {
  return key ? key.split(":").slice(1).join(":") : "";
}

export const DisplayDiscussionForMiniScreenProvider = ({ children }) => {
  const [displayDiscussionForMiniScreen, setDisplayDiscussionForMiniScreen] =
    useState(false);
  const [destinataire, setDestinataire] = useState(null);
  const [crew, setCrew] = useState(null);
  const [conversations, setConversations] = useState(false);
  const [inboxRecipientRole, setInboxRecipientRole] = useState(null);
  const [inboxRecipientPermissions, setInboxRecipientPermissions] = useState(null);
  const [activeRoomIdInbox, setActiveRoomIdInbox] = useState("");
  const [activeRoomIdGroupe, setActiveRoomIdGroupe] = useState("");
  const activeRawRoomIdInbox = rawRoomFromKey(activeRoomIdInbox);
  const activeRawRoomIdGroupe = rawRoomFromKey(activeRoomIdGroupe);
  const { user } = useContext(AuthContext);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;

  // --- Typing state centralisé ---
  const [typingInboxMap, setTypingInboxMap] = useState({});
  const [typingGroupeMap, setTypingGroupeMap] = useState({});

  useEffect(() => {
    if (destinataire && user) {
      const raw = user.userId > destinataire.userId
        ? `${user.userId}_${destinataire.userId}`
        : `${destinataire.userId}_${user.userId}`;
      const key = workspaceId ? roomKey(workspaceId, raw) : raw;
      setActiveRoomIdInbox(key);
    }
  }, [destinataire, user, workspaceId]);

  // Listeners typing centralisés
  useEffect(() => {
    const onTypingInbox = ({ userId, roomId }) => {
      setTypingInboxMap((prev) => ({ ...prev, [roomId]: userId }));
    };
    const onStopTypingInbox = (stoppeur) => {
      setTypingInboxMap((prev) => {
        const next = { ...prev };
        for (const key in next) {
          if (next[key] === stoppeur) delete next[key];
        }
        return next;
      });
    };
    const onTypingGroupe = ({ user: writer, roomId }) => {
      setTypingGroupeMap((prev) => {
        const current = prev[roomId] || [];
        if (current.some((u) => u.userId === writer.userId)) return prev;
        return { ...prev, [roomId]: [...current, writer] };
      });
    };
    const onStopTypingGroupe = ({ stoppeur, roomId }) => {
      setTypingGroupeMap((prev) => {
        const current = prev[roomId] || [];
        const filtered = current.filter((u) => u.userId !== stoppeur);
        return { ...prev, [roomId]: filtered };
      });
    };

    socket.on("userTypingInbox", onTypingInbox);
    socket.on("userStoppedTypingInbox", onStopTypingInbox);
    socket.on("userTypingGroupe", onTypingGroupe);
    socket.on("userStoppedTypingGroupe", onStopTypingGroupe);

    return () => {
      socket.off("userTypingInbox", onTypingInbox);
      socket.off("userStoppedTypingInbox", onStopTypingInbox);
      socket.off("userTypingGroupe", onTypingGroupe);
      socket.off("userStoppedTypingGroupe", onStopTypingGroupe);
    };
  }, []);

  // Mettre à jour crew en temps réel quand les infos du groupe changent
  const crewRef = useRef(crew);
  crewRef.current = crew;
  useEffect(() => {
    const handleGroupInfoUpdated = ({ roomId, groupe }) => {
      const current = crewRef.current;
      if (current && current.roomId === roomId) {
        setCrew((prev) => ({
          ...prev,
          nom: groupe.groupeInfo.nom,
          imageUrl: groupe.groupeInfo.imageUrl,
          description: groupe.groupeInfo.description,
        }));
      }
    };

    socket.on("groupe_info_updated", handleGroupInfoUpdated);
    return () => {
      socket.off("groupe_info_updated", handleGroupInfoUpdated);
    };
  }, [setCrew]);

  // Helper : clear typing quand un message arrive
  const clearTypingInbox = useCallback((emetteurId) => {
    setTypingInboxMap((prev) => {
      const next = { ...prev };
      for (const key in next) {
        if (next[key] === emetteurId) delete next[key];
      }
      return next;
    });
  }, []);

  const clearTypingGroupe = useCallback((roomId, emetteurId) => {
    setTypingGroupeMap((prev) => {
      const current = prev[roomId] || [];
      const filtered = current.filter((u) => u.userId !== emetteurId);
      return { ...prev, [roomId]: filtered };
    });
  }, []);

  return (
    <DisplayDiscussionForMiniScreenContext.Provider
      value={{
        displayDiscussionForMiniScreen,
        setDisplayDiscussionForMiniScreen,
        destinataire,
        setDestinataire,
        conversations,
        setConversations,
        activeRoomIdCrew: activeRoomIdGroupe,
        activeRoomIdGroupe,
        activeRawRoomIdGroupe,
        setActiveRoomIdCrew: setActiveRoomIdGroupe,
        setActiveRoomIdGroupe,
        crew,
        setCrew,
        activeRoomIdInbox,
        activeRawRoomIdInbox,
        setActiveRoomIdInbox,
        inboxRecipientRole,
        setInboxRecipientRole,
        inboxRecipientPermissions,
        setInboxRecipientPermissions,
        typingInboxMap,
        typingGroupeMap,
        clearTypingInbox,
        clearTypingGroupe,
      }}
    >
      <MessageProvider>{children}</MessageProvider>
    </DisplayDiscussionForMiniScreenContext.Provider>
  );
};
